import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Workshop, Technic } from "../types";
import { workshopService, technicService } from "../services/dataService";
import { turkishIncludes } from "../utils/formatters";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import WorkshopCostsModal from "../components/WorkshopCostsModal";
import { usePermissions } from "../context/PermissionContext";
import "./PurchaseOrders.css";
import "./Workshops.css";

/**
 * Atölye ve Teknikler — po-* dili. İki sekme (Atölyeler / Teknikler; `?tab=technics` ile doğrudan
 * teknik sekmesi), atölye maliyetleri WorkshopCostsModal'da, bağlantılar yerel modalda. Sözlük `ws-*`.
 */

type TabType = "workshops" | "technics";

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  filterOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18l-7 8v6l-4-2v-4L3 5Z" /></svg>,
  factory: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18M5 21V8l6-3v3l6-3v16M9 21v-4h4v4" /></svg>,
  tool: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a4 4 0 0 0-5.6 5.6L3 18.3V21h2.7l6.4-6.4a4 4 0 0 0 5.6-5.6l-2.6 2.6-2-2 2.6-2.3z" /></svg>,
  coin: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M9.5 9.3c0-1 1.1-1.8 2.5-1.8s2.5.7 2.5 1.8-1 1.5-2.5 1.9-2.5.8-2.5 1.9 1.1 1.6 2.5 1.6 2.5-.7 2.5-1.6" /></svg>,
  link: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5" /><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5" /></svg>,
  pin: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>,
};

/** Yerel modallar için Esc (yakalama evresi) — üstte Alert/Confirm açıkken devre dışı. */
const useEscClose = (open: boolean, onClose: () => void, disabled: boolean) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || disabled) return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onClose, disabled]);
};

const Workshops: React.FC = () => {
  const { has } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: TabType = searchParams.get("tab") === "technics" ? "technics" : "workshops";
  const setActiveTab = (tab: TabType) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "technics") next.set("tab", "technics");
    else next.delete("tab");
    setSearchParams(next, { replace: true });
  };
  const [costModalWorkshop, setCostModalWorkshop] = useState<Workshop | null>(
    null,
  );
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [technics, setTechnics] = useState<Technic[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState<Workshop | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [technicWorkshopFilter, setTechnicWorkshopFilter] = useState(""); // "" tümü, "none" atölyesiz, aksi atölye id

  // Teknik modal states
  const [isTechnicModalOpen, setIsTechnicModalOpen] = useState(false);
  const [editingTechnic, setEditingTechnic] = useState<Technic | null>(null);
  const [technicFormData, setTechnicFormData] = useState({
    name: "",
    category: "",
    description: "",
    workshopId: "",
  });

  // İlişki (bağlantı) modal state'leri
  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);
  const [selectedWorkshopForConnections, setSelectedWorkshopForConnections] =
    useState<Workshop | null>(null);
  const [workshopConnections, setWorkshopConnections] = useState<any[]>([]);
  const [connLoading, setConnLoading] = useState(false);
  const [connSearch, setConnSearch] = useState("");
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    location: "",
    displayOrder: 0,
  });

  // Alert ve Confirm Modal state'leri
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [workshopsData, technicsData] = await Promise.all([
        workshopService.getAll(),
        technicService.getAll(),
      ]);

      // Backend zaten sıralı döndürüyor ama emin olmak için:
      const sortedData = [...workshopsData].sort((a, b) => {
        const orderA = a.displayOrder ?? 999;
        const orderB = b.displayOrder ?? 999;
        if (orderA !== orderB) return orderA - orderB;
        return a.name.localeCompare(b.name, "tr");
      });
      setWorkshops(sortedData);
      setTechnics(technicsData);
    } catch (error) {
      console.error("❌ Failed to load data:", error);
      setAlertModal({
        isOpen: true,
        message: "Veriler yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  const loadWorkshops = loadData; // Geriye dönük uyumluluk için

  const openAddModal = () => {
    setEditingWorkshop(null);
    setFormData({
      name: "",
      location: "",
      displayOrder: 0,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (workshop: Workshop) => {
    setEditingWorkshop(workshop);
    setFormData({
      name: workshop.name,
      location: workshop.location || "",
      displayOrder: workshop.displayOrder || 0,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingWorkshop(null);
    setFormData({
      name: "",
      location: "",
      displayOrder: 0,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setAlertModal({
        isOpen: true,
        message: "Atölye adı zorunludur!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);

      const workshopData = {
        name: formData.name.trim(),
        location: formData.location.trim() || undefined,
        displayOrder: formData.displayOrder || 0,
      };

      if (editingWorkshop) {
        // Update
        await workshopService.update(editingWorkshop.workshopId, {
          ...workshopData,
          workshopId: editingWorkshop.workshopId,
        });
        setAlertModal({
          isOpen: true,
          message: "Atölye başarıyla güncellendi!",
          type: "success",
        });
      } else {
        // Create
        await workshopService.create(workshopData);
        setAlertModal({
          isOpen: true,
          message: "Atölye başarıyla oluşturuldu!",
          type: "success",
        });
      }

      closeModal();
      loadWorkshops();
    } catch (error: any) {
      console.error("❌ Failed to save workshop:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Atölye kaydedilemedi!";
      setAlertModal({ isOpen: true, message: errorMessage, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (workshop: Workshop) => {
    setConfirmModal({
      isOpen: true,
      message: `"${workshop.name}" atölyesini silmek istediğinizden emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await workshopService.delete(workshop.workshopId);
          setAlertModal({
            isOpen: true,
            message: "Atölye başarıyla silindi!",
            type: "success",
          });
          loadWorkshops();
        } catch (error: any) {
          console.error("❌ Failed to delete workshop:", error);
          setAlertModal({
            isOpen: true,
            message: "Atölye silinemedi!",
            type: "error",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const openConnectionModal = async (workshop: Workshop) => {
    setSelectedWorkshopForConnections(workshop);
    setIsConnectionModalOpen(true);
    setConnSearch("");
    setConnLoading(true);
    await loadWorkshopConnections(workshop.workshopId);
    setConnLoading(false);
  };

  const closeConnectionModal = () => {
    setIsConnectionModalOpen(false);
    setSelectedWorkshopForConnections(null);
    setWorkshopConnections([]);
    setConnSearch("");
  };

  const loadWorkshopConnections = async (workshopId: string) => {
    try {
      const connections = await workshopService.getConnections(workshopId);
      setWorkshopConnections(connections || []);
    } catch (error) {
      console.error("❌ Failed to load connections:", error);
      setWorkshopConnections([]);
    }
  };

  // Tek dokunuşla ilişki aç/kapat (anında uygulanır, sessiz)
  const toggleConnection = async (workshop: Workshop) => {
    if (!selectedWorkshopForConnections) return;
    const existing = workshopConnections.find(
      (c: any) => c.targetWorkshop?.workshopId === workshop.workshopId,
    );
    try {
      setTogglingId(workshop.workshopId);
      if (existing) {
        await workshopService.removeConnection(
          selectedWorkshopForConnections.workshopId,
          existing.connectionId,
        );
      } else {
        await workshopService.addConnection(
          selectedWorkshopForConnections.workshopId,
          { targetWorkshopId: workshop.workshopId, notes: "" },
        );
      }
      await loadWorkshopConnections(selectedWorkshopForConnections.workshopId);
    } catch (error: any) {
      console.error("❌ Failed to toggle connection:", error);
      const msg =
        error.response?.data || error.message || "Bağlantı güncellenemedi!";
      setAlertModal({ isOpen: true, message: typeof msg === "string" ? msg : "Bağlantı güncellenemedi!", type: "error" });
    } finally {
      setTogglingId(null);
    }
  };

  // Teknik işlemleri
  const openTechnicModal = () => {
    setEditingTechnic(null);
    setTechnicFormData({
      name: "",
      category: "",
      description: "",
      workshopId: technicWorkshopFilter && technicWorkshopFilter !== "none" ? technicWorkshopFilter : "",
    });
    setIsTechnicModalOpen(true);
  };

  const openEditTechnicModal = (technic: Technic) => {
    setEditingTechnic(technic);
    setTechnicFormData({
      name: technic.name,
      category: technic.category || "",
      description: technic.description || "",
      workshopId: technic.workshopId || "",
    });
    setIsTechnicModalOpen(true);
  };

  const closeTechnicModal = () => {
    setIsTechnicModalOpen(false);
    setEditingTechnic(null);
    setTechnicFormData({
      name: "",
      category: "",
      description: "",
      workshopId: "",
    });
  };

  const handleTechnicSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!technicFormData.name.trim()) {
      setAlertModal({
        isOpen: true,
        message: "Teknik adı zorunludur!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);

      // Convert empty strings to undefined for nullable Guid fields
      const cleanedData = {
        ...technicFormData,
        workshopId: technicFormData.workshopId || undefined,
        category: technicFormData.category || undefined,
        description: technicFormData.description || undefined,
      };

      if (editingTechnic) {
        await technicService.update(editingTechnic.technicId, cleanedData);
        setAlertModal({
          isOpen: true,
          message: "Teknik başarıyla güncellendi!",
          type: "success",
        });
      } else {
        await technicService.create(cleanedData);
        setAlertModal({
          isOpen: true,
          message: "Teknik başarıyla oluşturuldu!",
          type: "success",
        });
      }

      closeTechnicModal();
      loadData();
    } catch (error: any) {
      console.error("❌ Failed to save technic:", error);
      setAlertModal({
        isOpen: true,
        message: "Teknik kaydedilemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTechnicDelete = async (technic: Technic) => {
    setConfirmModal({
      isOpen: true,
      message: `"${technic.name}" tekniğini silmek istediğinizden emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await technicService.delete(technic.technicId);
          setAlertModal({
            isOpen: true,
            message: "Teknik başarıyla silindi!",
            type: "success",
          });
          loadData();
        } catch (error: any) {
          console.error("❌ Failed to delete technic:", error);
          setAlertModal({
            isOpen: true,
            message: "Teknik silinemedi!",
            type: "error",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Esc ile kapanma — üstte uyarı/onay penceresi varken onlar kapanır
  const overlayBusy = alertModal.isOpen || confirmModal.isOpen;
  useEscClose(isModalOpen, closeModal, overlayBusy || loading);
  useEscClose(isTechnicModalOpen, closeTechnicModal, overlayBusy || loading);
  useEscClose(isConnectionModalOpen, closeConnectionModal, overlayBusy);

  const techCountByWorkshop = useMemo(() => {
    const m = new Map<string, number>();
    technics.forEach((t) => { if (t.workshopId) m.set(t.workshopId, (m.get(t.workshopId) || 0) + 1); });
    return m;
  }, [technics]);
  const unassignedTechnics = technics.filter((t) => !t.workshopId).length;
  const categories = useMemo(
    () => Array.from(new Set(technics.map((t) => (t.category || "").trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, "tr")),
    [technics],
  );

  const filteredWorkshops = workshops.filter(
    (workshop) =>
      turkishIncludes(workshop.name, searchTerm) ||
      turkishIncludes(workshop.location || "", searchTerm) ||
      turkishIncludes(workshop.contactPerson || "", searchTerm),
  );

  const filteredTechnics = technics.filter((technic) => {
    if (technicWorkshopFilter === "none" && technic.workshopId) return false;
    if (technicWorkshopFilter && technicWorkshopFilter !== "none" && technic.workshopId !== technicWorkshopFilter) return false;
    return (
      turkishIncludes(technic.name, searchTerm) ||
      turkishIncludes(technic.category || "", searchTerm) ||
      turkishIncludes(technic.description || "", searchTerm)
    );
  });

  if (!loaded && workshops.length === 0) {
    return <PageLoader message="Atölyeler yükleniyor..." />;
  }

  const canCreate = has("workshops:create");
  const canUpdate = has("workshops:update");
  const canDelete = has("workshops:delete");
  const isWs = activeTab === "workshops";
  const filterOn = !!(searchTerm || (!isWs && technicWorkshopFilter));
  const resetFilters = () => { setSearchTerm(""); setTechnicWorkshopFilter(""); };
  const showTechnicsOf = (workshopId: string) => { setSearchTerm(""); setTechnicWorkshopFilter(workshopId); setActiveTab("technics"); };
  const workshopName = (id?: string) => workshops.find((w) => w.workshopId === id)?.name;

  return (
    <div className="po-page ws-page">
      <header className="po-head">
        <div>
          <span className="po-kicker">Atölye</span>
          <h1>Atölye ve Teknikler</h1>
          <p>Üretim atölyeleri, sıraları ve aralarındaki iş akışı bağlantıları; atölye maliyet kalemleri ve atölyelerde uygulanan teknikler.</p>
        </div>
        {canCreate && (
          <div className="po-head-actions">
            <button type="button" className="po-btn primary" onClick={isWs ? openAddModal : openTechnicModal}>
              {I.plus} {isWs ? "Yeni atölye" : "Yeni teknik"}
            </button>
          </div>
        )}
      </header>

      <nav className="po-tabs" role="tablist" aria-label="Atölye ve teknikler">
        <button type="button" role="tab" aria-selected={isWs} className={isWs ? "on" : ""} onClick={() => setActiveTab("workshops")}>
          Atölyeler <span>{workshops.length}</span>
        </button>
        <button type="button" role="tab" aria-selected={!isWs} className={!isWs ? "on" : ""} onClick={() => setActiveTab("technics")}>
          Teknikler <span>{technics.length}</span>
        </button>
      </nav>

      <section className={`po-card ${loading ? "dim" : ""}`}>
        <div className="po-toolbar">
          <div className="po-search">
            {I.search}
            <input
              aria-label={isWs ? "Atölye ara" : "Teknik ara"}
              placeholder={isWs ? "Atölye adı, lokasyon veya ilgili kişi ara" : "Teknik adı, kategori veya açıklama ara"}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && <button type="button" className="po-ib" aria-label="Aramayı temizle" onClick={() => setSearchTerm("")}>{I.close}</button>}
          </div>
          {!isWs && (
            <select className="po-select ws-filter" aria-label="Atölyeye göre süz" value={technicWorkshopFilter} onChange={(e) => setTechnicWorkshopFilter(e.target.value)}>
              <option value="">Tüm atölyeler</option>
              {unassignedTechnics > 0 && <option value="none">Atölyesi seçilmemiş ({unassignedTechnics})</option>}
              {workshops.map((w) => <option key={w.workshopId} value={w.workshopId}>{w.name} ({techCountByWorkshop.get(w.workshopId) || 0})</option>)}
            </select>
          )}
          {filterOn && <button type="button" className="po-clear" onClick={resetFilters}>{I.filterOff} Temizle</button>}
        </div>

        {isWs ? (
          <>
            <div className="po-card-head">
              <span><b>{filteredWorkshops.length}</b> atölye{filteredWorkshops.length !== workshops.length && <> / {workshops.length}</>}</span>
              <span className="po-hint po-hide-sm">Sıra numarası küçük olan önce gelir</span>
            </div>
            {filteredWorkshops.length === 0 ? (
              <div className="po-blank">
                <span className="po-blank-icon">{I.factory}</span>
                <b>{searchTerm ? "Aramayla eşleşen atölye yok" : "Henüz atölye yok"}</b>
                {searchTerm ? "Aramayı değiştirin." : "Siparişlerin geçeceği üretim atölyelerini tanımlayarak başlayın."}
                {!searchTerm && canCreate && <button type="button" className="po-btn primary" onClick={openAddModal}>{I.plus} İlk atölyeyi ekle</button>}
              </div>
            ) : (
              <div className="po-table-wrap">
                <table className="po-table">
                  <thead>
                    <tr>
                      <th className="num ws-ord">Sıra</th>
                      <th>Atölye</th>
                      <th className="num po-hide-sm">Teknik</th>
                      <th className="act"><span className="ws-sr">İşlemler</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredWorkshops.map((workshop) => {
                      const tc = techCountByWorkshop.get(workshop.workshopId) || 0;
                      return (
                        <tr key={workshop.workshopId} className="static">
                          <td className="num ws-ord">{workshop.displayOrder ? <span className="po-code">#{workshop.displayOrder}</span> : <span className="po-sub">—</span>}</td>
                          <td>
                            <div className="po-name">{workshop.name}</div>
                            {workshop.location && <div className="po-sub ws-loc">{I.pin}{workshop.location}</div>}
                            <div className="po-sub po-only-sm">{tc} teknik</div>
                          </td>
                          <td className="num po-hide-sm">
                            {tc ? <button type="button" className="po-link" title="Bu atölyenin tekniklerini göster" onClick={() => showTechnicsOf(workshop.workshopId)}>{tc}</button> : <span className="po-sub">—</span>}
                          </td>
                          <td className="act">
                            <div className="po-rowacts ws-acts">
                              <button type="button" className="po-btn small" title="Maliyet yönetimi" aria-label={`${workshop.name} maliyet yönetimi`} onClick={() => setCostModalWorkshop(workshop)}>{I.coin}<span className="po-hide-sm">Maliyet</span></button>
                              <button type="button" className="po-btn small" title="Bağlantılar" aria-label={`${workshop.name} bağlantıları`} onClick={() => openConnectionModal(workshop)}>{I.link}<span className="po-hide-sm">Bağlantılar</span></button>
                              {canUpdate && <button type="button" className="po-ib" title="Düzenle" aria-label={`${workshop.name} düzenle`} onClick={() => openEditModal(workshop)}>{I.edit}</button>}
                              {canDelete && <button type="button" className="po-ib danger" title="Sil" aria-label={`${workshop.name} sil`} onClick={() => handleDelete(workshop)}>{I.trash}</button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="po-card-head">
              <span><b>{filteredTechnics.length}</b> teknik{filteredTechnics.length !== technics.length && <> / {technics.length}</>}</span>
              <span className="po-hint po-hide-sm">{categories.length} kategori · {unassignedTechnics} teknikte atölye seçilmemiş</span>
            </div>
            {filteredTechnics.length === 0 ? (
              <div className="po-blank">
                <span className="po-blank-icon">{I.tool}</span>
                <b>{filterOn ? "Süzgeçle eşleşen teknik yok" : "Henüz teknik yok"}</b>
                {filterOn ? "Aramayı ya da atölye süzgecini değiştirin." : "Siparişlerde seçilecek baskı/işlem tekniklerini tanımlayın."}
                {filterOn
                  ? <button type="button" className="po-btn" onClick={resetFilters}>Süzgeci sıfırla</button>
                  : canCreate && <button type="button" className="po-btn primary" onClick={openTechnicModal}>{I.plus} İlk tekniği ekle</button>}
              </div>
            ) : (
              <div className="po-table-wrap">
                <table className="po-table">
                  <thead>
                    <tr>
                      <th>Teknik</th>
                      <th className="po-hide-sm">Kategori</th>
                      <th className="po-hide-sm">Atölye</th>
                      <th className="act"><span className="ws-sr">İşlemler</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTechnics.map((technic) => {
                      const wsName = technic.workshop?.name || workshopName(technic.workshopId);
                      return (
                        <tr key={technic.technicId} className="static">
                          <td>
                            <div className="po-name">{technic.name}</div>
                            {technic.description && <div className="po-sub ws-desc">{technic.description}</div>}
                            {(technic.category || wsName) && <div className="po-sub po-only-sm">{[technic.category, wsName].filter(Boolean).join(" · ")}</div>}
                          </td>
                          <td className="po-hide-sm">{technic.category ? <span className="po-badge muted">{technic.category}</span> : <span className="po-sub">—</span>}</td>
                          <td className="po-hide-sm">{wsName ? <span className="ws-wsname">{I.factory}{wsName}</span> : <span className="po-sub">—</span>}</td>
                          <td className="act">
                            <div className="po-rowacts">
                              {canUpdate && <button type="button" className="po-ib" title="Düzenle" aria-label={`${technic.name} düzenle`} onClick={() => openEditTechnicModal(technic)}>{I.edit}</button>}
                              {canDelete && <button type="button" className="po-ib danger" title="Sil" aria-label={`${technic.name} sil`} onClick={() => handleTechnicDelete(technic)}>{I.trash}</button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      {/* Atölye formu */}
      {isModalOpen && (
        <div className="po-overlay center" onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) closeModal(); }}>
          <form className="po-modal" role="dialog" aria-modal="true" aria-labelledby="ws-form-title" onSubmit={handleSubmit}>
            <div className="po-modal-head">
              <div><span className="po-kicker">Atölye</span><h3 id="ws-form-title">{editingWorkshop ? editingWorkshop.name : "Yeni atölye"}</h3></div>
              <button type="button" className="po-ib big" aria-label="Kapat" onClick={closeModal} disabled={loading}>{I.close}</button>
            </div>
            <div className="po-modal-body">
              <label className="po-field">
                <span>Atölye adı<em>zorunlu</em></span>
                <input type="text" autoFocus value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Örn. Ana Üretim Atölyesi" required />
              </label>
              <div className="po-grid2">
                <label className="po-field">
                  <span>Lokasyon</span>
                  <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="Örn. İstanbul, Türkiye" />
                </label>
                <label className="po-field">
                  <span>Sıra no</span>
                  <input type="number" min="0" value={formData.displayOrder} onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })} placeholder="0" />
                  <span className="po-fieldnote">Küçük değer listede ve panoda önce görünür. Varsayılan 0.</span>
                </label>
              </div>
            </div>
            <div className="po-modal-foot">
              <div className="po-foot-btns">
                <button type="button" className="po-btn" onClick={closeModal} disabled={loading}>Vazgeç</button>
                <button type="submit" className="po-btn primary" disabled={loading}>{loading ? "Kaydediliyor" : editingWorkshop ? "Kaydet" : "Atölyeyi oluştur"}</button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Teknik formu */}
      {isTechnicModalOpen && (
        <div className="po-overlay center" onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) closeTechnicModal(); }}>
          <form className="po-modal" role="dialog" aria-modal="true" aria-labelledby="ws-tech-title" onSubmit={handleTechnicSubmit}>
            <div className="po-modal-head">
              <div><span className="po-kicker">Teknik</span><h3 id="ws-tech-title">{editingTechnic ? editingTechnic.name : "Yeni teknik"}</h3></div>
              <button type="button" className="po-ib big" aria-label="Kapat" onClick={closeTechnicModal} disabled={loading}>{I.close}</button>
            </div>
            <div className="po-modal-body">
              <label className="po-field">
                <span>Teknik adı<em>zorunlu</em></span>
                <input type="text" autoFocus value={technicFormData.name} onChange={(e) => setTechnicFormData({ ...technicFormData, name: e.target.value })} placeholder="Örn. Dijital Baskı" required />
              </label>
              <div className="po-grid2">
                <label className="po-field">
                  <span>Kategori</span>
                  <input type="text" list="ws-tech-cats" value={technicFormData.category} onChange={(e) => setTechnicFormData({ ...technicFormData, category: e.target.value })} placeholder="Örn. Baskı Teknikleri" />
                  <datalist id="ws-tech-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
                </label>
                <label className="po-field">
                  <span>Atölye<em>isteğe bağlı</em></span>
                  <select value={technicFormData.workshopId} onChange={(e) => setTechnicFormData({ ...technicFormData, workshopId: e.target.value })}>
                    <option value="">Atölye seçilmedi</option>
                    {workshops.map((ws) => (
                      <option key={ws.workshopId} value={ws.workshopId}>{ws.name}</option>
                    ))}
                  </select>
                  <span className="po-fieldnote">Tekniğin hangi atölyede uygulandığı.</span>
                </label>
              </div>
              <label className="po-field">
                <span>Açıklama</span>
                <textarea value={technicFormData.description} onChange={(e) => setTechnicFormData({ ...technicFormData, description: e.target.value })} placeholder="Teknik hakkında kısa bir açıklama" rows={3} />
              </label>
            </div>
            <div className="po-modal-foot">
              <div className="po-foot-btns">
                <button type="button" className="po-btn" onClick={closeTechnicModal} disabled={loading}>Vazgeç</button>
                <button type="submit" className="po-btn primary" disabled={loading}>{loading ? "Kaydediliyor" : editingTechnic ? "Kaydet" : "Tekniği oluştur"}</button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Bağlantılar */}
      {isConnectionModalOpen && selectedWorkshopForConnections && (() => {
        const others = workshops.filter((w) => w.workshopId !== selectedWorkshopForConnections.workshopId);
        const isLinked = (id: string) => workshopConnections.some((c: any) => c.targetWorkshop?.workshopId === id);
        const connectedCount = others.filter((w) => isLinked(w.workshopId)).length;
        const visible = others.filter((w) => turkishIncludes(w.name, connSearch) || turkishIncludes(w.location || "", connSearch));
        return (
          <div className="po-overlay center" onMouseDown={(e) => { if (e.target === e.currentTarget) closeConnectionModal(); }}>
            <div className="po-modal wide" role="dialog" aria-modal="true" aria-labelledby="ws-conn-title">
              <div className="po-modal-head">
                <div><span className="po-kicker">Bağlantılar</span><h3 id="ws-conn-title">{selectedWorkshopForConnections.name}</h3></div>
                <button type="button" className="po-ib big" aria-label="Kapat" onClick={closeConnectionModal}>{I.close}</button>
              </div>
              <div className="po-modal-body">
                <p className="po-form-hint">İşin bu atölyeden geçebileceği atölyeleri seçin. Karta tıklayınca bağlanır, tekrar tıklayınca bağlantı kaldırılır; değişiklik anında kaydedilir.</p>
                {others.length === 0 ? (
                  <div className="po-blank small"><b>Bağlanabilecek başka atölye yok</b>Önce başka bir atölye ekleyin.</div>
                ) : (
                  <>
                    <div className="ws-rel-toolbar">
                      <div className="po-search">
                        {I.search}
                        <input aria-label="Atölye ara" placeholder="Atölye ara" value={connSearch} onChange={(e) => setConnSearch(e.target.value)} />
                        {connSearch && <button type="button" className="po-ib" aria-label="Aramayı temizle" onClick={() => setConnSearch("")}>{I.close}</button>}
                      </div>
                      <span className="po-hint"><b>{connectedCount}</b> / {others.length} bağlı</span>
                    </div>
                    {connLoading ? (
                      <div className="po-loading"><span className="po-spin" /> Bağlantılar yükleniyor</div>
                    ) : visible.length === 0 ? (
                      <div className="po-blank small"><b>Aramayla eşleşen atölye yok</b></div>
                    ) : (
                      <div className="ws-rel-grid">
                        {visible.map((w) => {
                          const linked = isLinked(w.workshopId);
                          const busy = togglingId === w.workshopId;
                          return (
                            <button
                              key={w.workshopId}
                              type="button"
                              className={`ws-rel-card ${linked ? "on" : ""}`}
                              onClick={() => toggleConnection(w)}
                              disabled={busy}
                              aria-pressed={linked}
                              title={linked ? "Bağlantıyı kaldır" : "Bağla"}
                            >
                              <span className="ws-rel-check">{busy ? <span className="po-spin" /> : linked ? I.check : I.plus}</span>
                              <span className="ws-rel-info">
                                <span className="ws-rel-name">{w.name}</span>
                                {w.location && <span className="po-sub ws-loc">{I.pin}{w.location}</span>}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="po-modal-foot">
                <div className="po-foot-btns">
                  <button type="button" className="po-btn primary" onClick={closeConnectionModal}>Kapat</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Atölye maliyet yönetimi modalı */}
      {costModalWorkshop && (
        <WorkshopCostsModal
          isOpen={!!costModalWorkshop}
          workshopId={costModalWorkshop.workshopId}
          workshopName={costModalWorkshop.name}
          onClose={() => setCostModalWorkshop(null)}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Silme onayı"
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText="Sil"
        cancelText="Vazgeç"
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

export default Workshops;
